/**
 * Idle-session arithmetic, kept free of React so the boundaries can be
 * asserted directly instead of through a rendered clock.
 *
 * A staff console holding internal tools should not sit signed in on an
 * unattended screen. Since ADR-0003 a reload no longer ends the session, so
 * this allowance — seeded from the store by `IdleGuard` — is one of the two
 * limits a session ends on; the other is the stored token's own `exp`.
 * Firebase mints an ID token with a one-hour life, so the two limits now sit
 * at about the same hour. ADR-0003 records which one ends which tab.
 */

/**
 * How long a session may sit idle before it is signed out.
 *
 * A product decision, not a design value, so it lives here rather than in the
 * design system. One hour, raised from fifteen minutes by EI-446.
 * `idle.test.ts` pins the value, so changing it stays a deliberate act.
 */
export const IDLE_LIMIT_MS = 60 * 60 * 1000

/**
 * How long before sign-out the warning appears.
 *
 * Five minutes before the limit. A product decision like the limit above, so
 * it lives here in code rather than as a design token: a duration the user is
 * counted down against is behaviour, and a value a test asserts should not
 * have to be read back out of CSS.
 */
export const IDLE_WARNING_MS = 5 * 60 * 1000

/**
 * Dispatched on `window` when the idle warning appears.
 *
 * The nav sheet is a modal over `#root`. Listeners close it so Stay is
 * reachable, and so the warning is not aria-hidden with the rest of the app.
 */
export const IDLE_WARNING_EVENT = 'internal-console:idle-warning'

/** Where an idle session currently stands. */
export type IdlePhase = 'active' | 'warning' | 'expired'

/**
 * Classify how long a session has been idle.
 *
 * A negative elapsed time — a clock that stepped backwards, or a timer that
 * fired early — is treated as active. Signing someone out because their
 * machine adjusted its clock is worse than waiting another interval.
 *
 * @param idleForMs - Milliseconds since the last user interaction.
 * @param limitMs - Idle allowance before sign-out.
 * @param warningMs - How long before sign-out to warn.
 * @returns The current phase.
 */
export function idlePhase(
  idleForMs: number,
  limitMs: number = IDLE_LIMIT_MS,
  warningMs: number = IDLE_WARNING_MS
): IdlePhase {
  if (idleForMs >= limitMs) {
    return 'expired'
  }
  if (idleForMs >= limitMs - warningMs) {
    return 'warning'
  }

  return 'active'
}

/**
 * How long until the phase changes, for scheduling a single timer rather than
 * polling on an interval.
 *
 * @param idleForMs - Milliseconds since the last user interaction.
 * @param limitMs - Idle allowance before sign-out.
 * @param warningMs - How long before sign-out to warn.
 * @returns Milliseconds until the next phase, or null when already expired
 *   and there is nothing further to wait for.
 */
export function msUntilPhaseChange(
  idleForMs: number,
  limitMs: number = IDLE_LIMIT_MS,
  warningMs: number = IDLE_WARNING_MS
): number | null {
  const phase = idlePhase(idleForMs, limitMs, warningMs)
  if (phase === 'expired') {
    return null
  }
  if (phase === 'warning') {
    return limitMs - idleForMs
  }

  return limitMs - warningMs - idleForMs
}

/**
 * Seconds left before sign-out, for the warning's countdown.
 *
 * Rounded up, so a session with 400ms left reads "1" rather than "0" — a
 * countdown that shows zero while the session is still alive reads as a bug.
 *
 * @param idleForMs - Milliseconds since the last user interaction.
 * @param limitMs - Idle allowance before sign-out.
 * @returns Whole seconds remaining, never below zero.
 */
export function secondsUntilSignOut(
  idleForMs: number,
  limitMs: number = IDLE_LIMIT_MS
): number {
  return Math.max(0, Math.ceil((limitMs - idleForMs) / 1000))
}

/**
 * The countdown as minutes and seconds, for the warning banner.
 *
 * A warning window of more than a minute reads badly as a bare second count.
 * `m:ss` is the idiom a reader already knows, and it stays legible whatever
 * the window is set to.
 *
 * @param secondsLeft - Whole seconds remaining, from `secondsUntilSignOut`.
 * @returns The countdown as `m:ss`, never below `0:00`.
 */
export function formatSignOutCountdown(secondsLeft: number): string {
  const safe = Math.max(0, Math.floor(secondsLeft))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60

  return `${minutes}:${String(seconds).padStart(2, '0')}`
}
